// ROOM 404: 24-Hour Alias-to-Alias Direct Messaging
// Real-Time AES-256-GCM Encrypted Ephemeral Chat, Consent Requests,
// 24-Hour Expiry Indicators, Offline Replay Protection, and Safe Member Controls.

import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Check,
  Clock,
  Info,
  Lock,
  MessageSquare,
  Plus,
  RefreshCw,
  Send,
  Shield,
  ShieldAlert,
  Trash2,
  UserCheck,
  UserX,
  WifiOff,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api';
import type {
  DirectConversation,
  DirectMessageItem,
  DirectMessageRequest,
} from '../types';

interface DirectMessagesProps {
  initialPeerAlias?: string | null;
  onBackToRooms?: () => void;
  onOpenReport?: (targetId?: string) => void;
}

export const DirectMessages: React.FC<DirectMessagesProps> = ({
  initialPeerAlias,
  onBackToRooms,
  onOpenReport,
}) => {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState<'conversations' | 'requests'>('conversations');
  const [conversations, setConversations] = useState<DirectConversation[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<DirectMessageRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<DirectMessageRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active Chat State
  const [activeConv, setActiveConv] = useState<DirectConversation | null>(null);
  const [messages, setMessages] = useState<DirectMessageItem[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // New Request Modal State
  const [showNewModal, setShowNewModal] = useState(Boolean(initialPeerAlias));
  const [targetAlias, setTargetAlias] = useState(initialPeerAlias || '');
  const [initialMsgText, setInitialMsgText] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Privacy Info Drawer
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Online / offline listeners
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch conversations and requests
  const fetchData = async () => {
    try {
      setError(null);
      const [convsRes, reqsRes] = await Promise.all([
        apiRequest<{ conversations: DirectConversation[] }>('/api/dm/conversations'),
        apiRequest<{
          incoming: DirectMessageRequest[];
          outgoing: DirectMessageRequest[];
        }>('/api/dm/requests'),
      ]);
      setConversations(convsRes.conversations || []);
      setIncomingRequests(reqsRes.incoming || []);
      setOutgoingRequests(reqsRes.outgoing || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load direct messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-scroll on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load messages when conversation is opened
  const openConversation = async (conv: DirectConversation) => {
    setActiveConv(conv);
    setLoadingMessages(true);
    try {
      const res = await apiRequest<{ messages: DirectMessageItem[] }>(
        `/api/dm/conversations/${conv.id}/messages?limit=40`
      );
      setMessages(res.messages || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load conversation history');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Poll active conversation periodically if online
  useEffect(() => {
    if (!activeConv || isOffline) return;
    const interval = setInterval(async () => {
      try {
        const res = await apiRequest<{ messages: DirectMessageItem[] }>(
          `/api/dm/conversations/${activeConv.id}/messages?limit=40`
        );
        setMessages((prev) => {
          // Merge deduplicated
          const existingIds = new Set(prev.map((m) => m.id));
          const newItems = (res.messages || []).filter((m) => !existingIds.has(m.id));
          if (newItems.length === 0) return prev;
          return [...prev, ...newItems];
        });
      } catch {
        // Silently continue on background poll error
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [activeConv, isOffline]);

  // Send Message with client-side idempotency key for replay defense
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeConv || !inputText.trim() || sending) return;

    const trimmed = inputText.trim();
    const idempotencyKey = crypto.randomUUID();
    setSending(true);

    // Optimistic item
    const tempId = `temp_${Date.now()}`;
    const optimisticItem: DirectMessageItem = {
      id: tempId,
      conversationId: activeConv.id,
      senderId: session?.userId || '',
      senderAlias: session?.profile?.alias || 'Me',
      senderAvatar: session?.profile?.avatarUrl || '',
      isSelf: true,
      body: trimmed,
      createdAt: Date.now(),
      expiresAt: Date.now() + 86400000,
    };

    setMessages((prev) => [...prev, optimisticItem]);
    setInputText('');

    try {
      const res = await apiRequest<{ message: DirectMessageItem; deduplicated?: boolean }>(
        `/api/dm/conversations/${activeConv.id}/messages`,
        {
          method: 'POST',
          body: {
            body: trimmed,
            idempotencyKey,
          },
        }
      );

      // Replace optimistic item with server authoritative item
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...res.message, isSelf: true } : m))
      );
    } catch (err: any) {
      // Revert optimistic on error
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setError(err?.message || 'Failed to deliver message');
    } finally {
      setSending(false);
    }
  };

  // Create New Request
  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetAlias.trim() || !initialMsgText.trim() || modalLoading) return;

    setModalLoading(true);
    setModalError(null);

    try {
      const res = await apiRequest<{
        conversationId: string;
        requestId: string;
        status: string;
      }>('/api/dm/request', {
        method: 'POST',
        body: {
          targetAlias: targetAlias.trim(),
          initialMessage: initialMsgText.trim(),
          idempotencyKey: crypto.randomUUID(),
        },
      });

      setShowNewModal(false);
      setTargetAlias('');
      setInitialMsgText('');
      await fetchData();
    } catch (err: any) {
      setModalError(err?.message || 'Failed to initiate conversation request');
    } finally {
      setModalLoading(false);
    }
  };

  // Accept Request
  const handleAcceptRequest = async (requestId: string) => {
    try {
      await apiRequest(`/api/dm/requests/${requestId}/accept`, { method: 'POST' });
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to accept message request');
    }
  };

  // Decline Request
  const handleDeclineRequest = async (requestId: string) => {
    try {
      await apiRequest(`/api/dm/requests/${requestId}/decline`, { method: 'POST' });
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to decline message request');
    }
  };

  // Block User
  const handleBlockUser = async (peerId: string) => {
    if (!window.confirm('Block this member? All communications and deliveries will be immediately terminated.')) {
      return;
    }

    try {
      await apiRequest('/api/dm/block', {
        method: 'POST',
        body: { targetUserId: peerId },
      });
      setActiveConv(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to block member');
    }
  };

  // Delete Conversation
  const handleDeleteConversation = async (convId: string) => {
    if (!window.confirm('Delete this conversation? Messages will be permanently erased.')) {
      return;
    }

    try {
      await apiRequest(`/api/dm/conversations/${convId}`, { method: 'DELETE' });
      setActiveConv(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete conversation');
    }
  };

  // Active Chat Screen
  if (activeConv) {
    return (
      <div className="flex flex-col h-[calc(100vh-60px)] bg-[#090a0f] text-gray-100 max-w-md mx-auto">
        {/* Chat Top App Bar */}
        <div className="px-4 py-3 bg-[#0c0e17] border-b border-[#1f2433] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setActiveConv(null)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-[#181b26] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-xs font-bold text-emerald-400 overflow-hidden">
                {activeConv.peerAvatar ? (
                  <img src={activeConv.peerAvatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  activeConv.peerAlias.slice(0, 2).toUpperCase()
                )}
              </div>
              <div>
                <div className="font-semibold text-sm text-white flex items-center space-x-1.5">
                  <span>{activeConv.peerAlias}</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-400 font-mono border border-emerald-500/20">
                    24h TTL
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 flex items-center space-x-1">
                  <Lock className="w-3 h-3 text-emerald-500" />
                  <span>AES-256-GCM at rest</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Menu Buttons */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onOpenReport?.(activeConv.peerId)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-[#181b26] transition-colors"
              title="Report user"
            >
              <AlertTriangle className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleBlockUser(activeConv.peerId)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-[#181b26] transition-colors"
              title="Block user"
            >
              <Ban className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteConversation(activeConv.id)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-[#181b26] transition-colors"
              title="Delete conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 24-Hour Expiry Banner */}
        <div className="px-4 py-2 bg-emerald-950/20 border-b border-emerald-500/10 flex items-center justify-between text-xs text-emerald-400">
          <div className="flex items-center space-x-2">
            <Clock className="w-3.5 h-3.5 text-emerald-400 animate-spin-slow" />
            <span>24-Hour Expiration Active: Messages self-destruct after 24 hours.</span>
          </div>
          <button
            onClick={() => setShowPrivacyNotice(true)}
            className="text-[11px] underline text-emerald-300 hover:text-emerald-100"
          >
            Policy
          </button>
        </div>

        {/* Offline Warning Banner */}
        {isOffline && (
          <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center space-x-2 text-xs text-amber-400">
            <WifiOff className="w-3.5 h-3.5 text-amber-400" />
            <span>Offline mode. Messages will be retried without duplicates on reconnect.</span>
          </div>
        )}

        {/* Message Feed */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loadingMessages ? (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400 space-y-2">
              <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
              <span className="text-xs">Decrypting ephemeral messages...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-xs">
              No recent messages in this session. All messages expire permanently after 24 hours.
            </div>
          ) : (
            messages.map((m) => {
              const isSelf = m.isSelf || m.senderId === session?.userId;
              const timeLeftMinutes = Math.max(0, Math.floor((m.expiresAt - Date.now()) / 60000));
              const hoursLeft = Math.floor(timeLeftMinutes / 60);

              return (
                <div
                  key={m.id}
                  className={`flex flex-col ${isSelf ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      isSelf
                        ? 'bg-emerald-600 text-white rounded-br-none shadow-[0_2px_12px_rgba(16,185,129,0.2)]'
                        : 'bg-[#181b26] text-gray-200 border border-[#272c3d] rounded-bl-none'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  </div>
                  <div className="flex items-center space-x-1.5 mt-1 text-[10px] text-gray-500 font-mono">
                    <span>
                      {new Date(m.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span>•</span>
                    <span className="text-emerald-500/80">
                      {hoursLeft > 0 ? `${hoursLeft}h left` : `${timeLeftMinutes}m left`}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Bar */}
        {activeConv.status === 'accepted' ? (
          <form
            onSubmit={handleSendMessage}
            className="p-3 bg-[#0c0e17] border-t border-[#1f2433] flex items-center space-x-2"
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Send an encrypted 24h message..."
              disabled={sending}
              maxLength={2000}
              className="flex-1 bg-[#12141c] border border-[#272c3d] rounded-xl px-3.5 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-emerald-500"
            />
            <button
              type="submit"
              disabled={!inputText.trim() || sending}
              className="p-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold disabled:opacity-40 disabled:hover:bg-emerald-500 transition-colors shadow-[0_0_10px_rgba(16,185,129,0.3)]"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <div className="p-3 bg-[#12141c] border-t border-[#1f2433] text-center text-xs text-amber-400 font-mono">
            {activeConv.status === 'pending'
              ? 'Message request pending. Recipient must accept before further messages can be delivered.'
              : `Conversation is currently ${activeConv.status}. Further communication restricted.`}
          </div>
        )}
      </div>
    );
  }

  // Conversations & Requests Dashboard Screen
  return (
    <div className="max-w-md mx-auto px-4 py-3 space-y-4">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {onBackToRooms && (
            <button
              onClick={onBackToRooms}
              className="p-1 rounded-lg text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div>
            <h1 className="text-base font-bold text-white flex items-center space-x-2">
              <MessageSquare className="w-4 h-4 text-emerald-400" />
              <span>Direct Messages</span>
            </h1>
            <p className="text-[11px] text-gray-400">Ephemeral alias-to-alias communication</p>
          </div>
        </div>

        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 text-xs font-semibold transition-all shadow-[0_0_12px_rgba(16,185,129,0.15)]"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>New</span>
        </button>
      </div>

      {/* Prominent 24-Hour Expiration & Non-E2EE Privacy Banner */}
      <div className="p-3.5 rounded-xl bg-[#10131f] border border-[#20273a] relative overflow-hidden">
        <div className="flex items-start space-x-3">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mt-0.5">
            <Clock className="w-4 h-4" />
          </div>
          <div className="flex-1 text-xs">
            <div className="font-semibold text-gray-100 flex items-center space-x-2">
              <span>24-Hour Self-Destruct Lifecycle</span>
              <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1 rounded font-mono">
                AES-256-GCM
              </span>
            </div>
            <p className="text-gray-400 mt-1 leading-relaxed">
              Messages and attachments automatically expire after 24 hours. Messages are server-encrypted at rest with fresh IV per message.
            </p>
            <div className="mt-2 flex items-center space-x-3 text-[11px]">
              <button
                onClick={() => setShowPrivacyNotice(true)}
                className="text-emerald-400 hover:underline flex items-center space-x-1"
              >
                <Info className="w-3 h-3" />
                <span>Read explicit privacy & moderation notice</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Segmented Control: Conversations vs Requests */}
      <div className="flex p-1 bg-[#12141c] border border-[#1f2433] rounded-xl text-xs font-semibold">
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex-1 py-1.5 rounded-lg transition-all ${
            activeTab === 'conversations'
              ? 'bg-[#1e2333] text-white shadow'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Conversations ({conversations.length})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`flex-1 py-1.5 rounded-lg transition-all flex items-center justify-center space-x-1.5 ${
            activeTab === 'requests'
              ? 'bg-[#1e2333] text-white shadow'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <span>Requests</span>
          {incomingRequests.length > 0 && (
            <span className="w-4 h-4 rounded-full bg-emerald-500 text-black text-[10px] font-bold flex items-center justify-center">
              {incomingRequests.length}
            </span>
          )}
        </button>
      </div>

      {/* Main Tab Content */}
      {loading ? (
        <div className="py-12 flex flex-col items-center justify-center text-gray-400 space-y-2">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
          <span className="text-xs">Loading sessions...</span>
        </div>
      ) : activeTab === 'conversations' ? (
        <div className="space-y-2">
          {conversations.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-xs border border-dashed border-[#1f2433] rounded-xl p-6">
              <MessageSquare className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p>No active conversations.</p>
              <button
                onClick={() => setShowNewModal(true)}
                className="mt-3 text-emerald-400 underline"
              >
                Start a conversation from a public alias
              </button>
            </div>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                onClick={() => openConversation(c)}
                className="p-3 rounded-xl bg-[#10131f] border border-[#1f2433] hover:border-emerald-500/40 cursor-pointer transition-all flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-sm font-bold text-emerald-400 overflow-hidden">
                    {c.peerAvatar ? (
                      <img src={c.peerAvatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      c.peerAlias.slice(0, 2).toUpperCase()
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white flex items-center space-x-2">
                      <span>{c.peerAlias}</span>
                      {c.status === 'pending' && (
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-400 font-mono">
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {c.lastMessageAt ? (
                        <span>Active {new Date(c.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      ) : (
                        'No messages sent yet'
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-[10px] text-emerald-500 font-mono bg-emerald-500/10 px-1.5 py-0.5 rounded">
                    24h TTL
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConversation(c.id);
                    }}
                    className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg"
                    title="Delete conversation"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Requests Tab View */
        <div className="space-y-4">
          {/* Incoming */}
          <div>
            <h2 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider font-mono">
              Incoming Requests ({incomingRequests.length})
            </h2>
            {incomingRequests.length === 0 ? (
              <p className="text-xs text-gray-500 p-4 bg-[#10131f] rounded-xl border border-[#1f2433]">
                No pending incoming message requests.
              </p>
            ) : (
              <div className="space-y-2">
                {incomingRequests.map((req) => (
                  <div
                    key={req.id}
                    className="p-3.5 rounded-xl bg-[#10131f] border border-[#272c3d] space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2.5">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-xs font-bold text-emerald-400">
                          {req.senderAlias?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">{req.senderAlias}</div>
                          <div className="text-[11px] text-gray-400">
                            Sent {new Date(req.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <span className="text-[10px] text-amber-400 font-mono bg-amber-500/10 px-1.5 py-0.5 rounded">
                        Consent Needed
                      </span>
                    </div>

                    {req.previewText && (
                      <div className="p-2.5 rounded-lg bg-[#0a0c12] border border-[#1e2333] text-xs text-gray-300 italic">
                        "{req.previewText}"
                      </div>
                    )}

                    {/* Controls: Accept, Decline, Block, Report */}
                    <div className="flex items-center space-x-2 pt-1">
                      <button
                        onClick={() => handleAcceptRequest(req.id)}
                        className="flex-1 py-1.5 rounded-lg bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400 flex items-center justify-center space-x-1"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Accept</span>
                      </button>
                      <button
                        onClick={() => handleDeclineRequest(req.id)}
                        className="py-1.5 px-3 rounded-lg bg-[#1c202d] text-gray-300 text-xs font-medium hover:bg-[#252a3b] flex items-center justify-center"
                        title="Decline request"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      {req.senderId && (
                        <>
                          <button
                            onClick={() => handleBlockUser(req.senderId!)}
                            className="py-1.5 px-3 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 text-xs hover:bg-red-500/20 flex items-center justify-center"
                            title="Block member"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onOpenReport?.(req.senderId)}
                            className="py-1.5 px-3 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs hover:bg-amber-500/20 flex items-center justify-center"
                            title="Report member"
                          >
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing */}
          {outgoingRequests.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider font-mono">
                Outgoing Requests ({outgoingRequests.length})
              </h2>
              <div className="space-y-2">
                {outgoingRequests.map((req) => (
                  <div
                    key={req.id}
                    className="p-3 rounded-xl bg-[#10131f] border border-[#1f2433] flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-7 h-7 rounded-full bg-gray-800 text-gray-300 flex items-center justify-center text-xs font-bold">
                        {req.recipientAlias?.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="text-xs font-medium text-white">{req.recipientAlias}</div>
                    </div>
                    <span className="text-[10px] text-gray-400 font-mono">
                      Awaiting recipient consent
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Conversation Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f111a] border border-[#272c3d] rounded-2xl w-full max-w-sm p-4 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-white flex items-center space-x-2">
                <MessageSquare className="w-4 h-4 text-emerald-400" />
                <span>Start Direct Conversation</span>
              </h3>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {modalError && (
              <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                {modalError}
              </div>
            )}

            <form onSubmit={handleCreateRequest} className="space-y-3">
              <div>
                <label className="text-xs text-gray-300 font-semibold mb-1 block">
                  Recipient Public Alias
                </label>
                <input
                  type="text"
                  value={targetAlias}
                  onChange={(e) => setTargetAlias(e.target.value)}
                  placeholder="e.g. EchoRaven"
                  disabled={modalLoading}
                  required
                  className="w-full bg-[#151824] border border-[#272c3d] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 font-semibold mb-1 block">
                  Initial Message (Consent Request)
                </label>
                <textarea
                  value={initialMsgText}
                  onChange={(e) => setInitialMsgText(e.target.value)}
                  placeholder="Introduce yourself. Recipient must accept before further messages can be delivered."
                  rows={3}
                  disabled={modalLoading}
                  required
                  maxLength={2000}
                  className="w-full bg-[#151824] border border-[#272c3d] rounded-xl p-3 text-sm text-white focus:outline-none focus:border-emerald-500 resize-none"
                />
              </div>

              <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-400 flex items-center space-x-2">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Expires 24 hours after delivery. Encrypted at rest.</span>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 py-2 rounded-xl bg-[#1c202d] text-gray-300 text-xs font-semibold hover:bg-[#252a3b]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalLoading || !targetAlias.trim() || !initialMsgText.trim()}
                  className="flex-1 py-2 rounded-xl bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400 disabled:opacity-40"
                >
                  {modalLoading ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Explicit Privacy Notice Modal */}
      {showPrivacyNotice && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f111a] border border-[#272c3d] rounded-2xl w-full max-w-sm p-4 space-y-4 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-[#1f2433] pb-3">
              <h3 className="font-bold text-sm text-white flex items-center space-x-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Explicit Privacy & Security Model</span>
              </h3>
              <button
                onClick={() => setShowPrivacyNotice(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="text-xs text-gray-300 space-y-3 leading-relaxed">
              <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                <strong>1. Encryption at Rest:</strong> Messages are stored in D1 encrypted with AES-256-GCM using a unique 12-byte initialization vector (IV) per message.
              </div>

              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300">
                <strong>2. Not End-to-End Encryption (E2EE):</strong> This is server-side envelope encryption at rest. Plaintext exists transiently in Worker memory during authorized delivery to participants.
              </div>

              <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                <strong>3. 24-Hour Expiration:</strong> All messages, cryptographic envelopes, and attachments are permanently pruned after 24 hours by automated retention cycles.
              </div>

              <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300">
                <strong>4. Moderation & Evidence:</strong> Moderation is handled through user-submitted evidence reports rather than indefinite platform archives.
              </div>
            </div>

            <button
              onClick={() => setShowPrivacyNotice(false)}
              className="w-full py-2 rounded-xl bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400"
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
